import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { dataCacheService } from '../services/DataCacheService';
import './AdminSettingsView.css';

function AdminSettingsView({ adminData }) {
  // School-scoped settings doc — keeps each school's info separate and lets
  // Firestore rules pass (the doc carries its schoolId).
  const settingsId = adminData?.schoolId ? `school_${adminData.schoolId}` : 'general';
  const [settings, setSettings] = useState({
    schoolName: 'KN Education Portal',
    schoolEmail: '',
    schoolPhone: '',
    schoolAddress: '',
    notificationEnabled: true,
    maintenanceMode: false,
    allowParentRegistration: true,
    allowTeacherRegistration: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    console.log('🔥 AdminSettings: Loading settings');
    try {
      const settingsData = await dataCacheService.getDoc(`settings/${settingsId}`, doc(db, 'settings', settingsId));
      if (settingsData) {
        console.log('🔥 AdminSettings: Settings loaded', settingsData);
        setSettings(prev => ({ ...prev, ...settingsData }));
      } else {
        console.log('🔥 AdminSettings: No settings found, using defaults');
      }
    } catch (error) {
      console.error('🔥 AdminSettings: Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await setDoc(doc(db, 'settings', settingsId), {
        ...settings,
        schoolId: adminData?.schoolId || null,
        updatedAt: new Date().toISOString()
      });
      dataCacheService.invalidatePath('settings');
      setMessage('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-settings-view">
      <div className="view-header">
        <h2>Settings</h2>
      </div>

      <form onSubmit={handleSave} className="settings-form">
        <h3>School Information</h3>
        <div className="form-group">
          <label>School Name</label>
          <input
            type="text"
            value={settings.schoolName}
            onChange={(e) => setSettings({ ...settings, schoolName: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>School Email</label>
          <input
            type="email"
            value={settings.schoolEmail}
            onChange={(e) => setSettings({ ...settings, schoolEmail: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>School Phone</label>
          <input
            type="tel"
            value={settings.schoolPhone}
            onChange={(e) => setSettings({ ...settings, schoolPhone: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>School Address</label>
          <textarea
            value={settings.schoolAddress}
            onChange={(e) => setSettings({ ...settings, schoolAddress: e.target.value })}
            rows={3}
          />
        </div>

        <h3>Portal Settings</h3>
        <div className="form-group toggle">
          <label>
            <input
              type="checkbox"
              checked={settings.notificationEnabled}
              onChange={(e) => setSettings({ ...settings, notificationEnabled: e.target.checked })}
            />
            Enable Notifications
          </label>
        </div>
        <div className="form-group toggle">
          <label>
            <input
              type="checkbox"
              checked={settings.maintenanceMode}
              onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
            />
            Maintenance Mode
          </label>
        </div>
        <div className="form-group toggle">
          <label>
            <input
              type="checkbox"
              checked={settings.allowParentRegistration}
              onChange={(e) => setSettings({ ...settings, allowParentRegistration: e.target.checked })}
            />
            Allow Parent Registration
          </label>
        </div>
        <div className="form-group toggle">
          <label>
            <input
              type="checkbox"
              checked={settings.allowTeacherRegistration}
              onChange={(e) => setSettings({ ...settings, allowTeacherRegistration: e.target.checked })}
            />
            Allow Teacher Registration
          </label>
        </div>

        {message && (
          <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminSettingsView;
